import {
  CountryEntry,
  countries,
  useLazyQuery,
  useMutation,
  useMyUrl,
  useTranslation,
} from '@gimlite/common';
import {
  BadgeType,
  FormType,
  Segmented,
  SegmentedType,
  Widget,
  WidgetGroupType,
  Zone,
} from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArticleCodeStatus,
  ContractEntity,
  Mutation,
  MutationUpdateContractArgs,
  ProductCategoryEnum,
  Query,
  QueryProductsArgs,
} from '../../client/graphql';
import { contractSessionsGql } from '../../common/gql/contract-sessions.gql';
import { getProductsOptionsGql } from '../../common/gql/get-products-options.gql';
import { updateContractGql } from '../../common/gql/update-contract.gql';
import { iconGlobalEntity } from '../../common/mapper/icon.mapper';
import {
  ContractBadgesType,
  useContractBadges,
} from '../../common/utils/contract-badges.hook';
import { ProfilDetails } from '../../components/profil-details/profil-details.component';
import { config } from '../../config';
import { PoolContractAction } from '../action/pool-contract.action';
import { ContractHistoryList } from '../list/history.list';
import { PoolAdminList } from '../list/pool-admin.list';
import { SessionList } from '../list/session.list';

type Status = 'ACTIVE' | 'SUSPENDED' | 'EXPIRED';

class Static {
  static statusBadgeColorMap: Record<Status, BadgeType.Config.Color> = {
    ACTIVE: 'disabled',
    SUSPENDED: 'warn',
    EXPIRED: 'disabled',
  };
}

export type MasterPoolContractDetailsProps = {
  contract: ContractEntity;
  widget?: {
    title?: string;
    state?: WidgetGroupType.Props['state'];
  };
};

function extractFormData(
  contract: ContractEntity,
  t: (key: string) => string,
): FormType.Data.Value {
  const {
    startDate,
    stopDate,
    name1,
    name2,
    ospContractId,
    address,
    spacesAllocated,
    description,
    reference,
    spacesOccupied,
    contractCount,
    productId,
    articleCode,
  } = contract;

  const { address1, address2, city, country, zipcode } = address || {
    address1: '',
    address2: '',
    city: '',
    country: '',
    zipcode: '',
  };

  return {
    name1,
    name2,
    code: ospContractId,
    reference: reference,
    streetName: address1,
    streetNumber: address2,
    zipCode: zipcode,
    city,
    country: country,
    product: productId,
    noOfSpace:
      contractCount === 1
        ? [`${spacesOccupied}`, `${spacesAllocated}`]
        : [`/${spacesOccupied}`, `${spacesAllocated}`],
    startDate,
    endDate: stopDate,
    description,
    articleCode,
  };
}

export const MasterPoolContractDetails = observer(
  ({ widget, contract }: MasterPoolContractDetailsProps) => {
    const { t, lang } = useTranslation();

    const [updateContractCall] = useMutation<
      { updateContract: Mutation['updateContract'] },
      MutationUpdateContractArgs
    >(updateContractGql);

    const [productsCall] = useLazyQuery<
      { products: Query['products'] },
      QueryProductsArgs
    >(getProductsOptionsGql);

    const [segmented, setSegmented] =
      useState<SegmentedType.Data.Selected>('admin');

    const [form, setForm] = useState<FormType.Data.Value>(
      extractFormData(contract, t),
    );
    const [data, setData] = useState<ContractEntity | null>(contract);

    const [productOptions, setProductOptions] = useState<any[]>([]);

    const countryOptions = useMemo(() => {
      if (!countries || !lang || !countries[lang]) return [];

      return countries[lang].map(({ value, label }: CountryEntry) => ({
        label,
        value: value.ISO3,
      }));
    }, [countries, lang]);
    const { setParamsUrl } = useMyUrl({});

    useEffect(() => {
      setSegmented('admin');
      setForm(extractFormData(contract, t));
    }, [contract, lang]);

    const handleSubmit = ({
      name1,
      name2,
      startDate,
      endDate,
      streetNumber,
      streetName,
      zipCode,
      city,
      country,
      description,
      reference,
      noOfSpace,
      product,
      articleCode,
      ...rest
    }: FormType.Data.Value) => {
      let updatedContract = {
        name1: name1 || '',
        name2: name2 || '',
        startDate,
        stopDate: endDate,
        spacesAllocated: Number(noOfSpace[1]),
        address: {
          address1: streetName,
          address2: streetNumber,
          city,
          country,
          zipcode: zipCode,
        },
        description: description || '',
        reference: reference,
        productId: product,
        articleCode,
      } as Partial<ContractEntity>;
      if (
        contract.contractCount === 1 &&
        contract.spacesOccupied !== Number(noOfSpace[0])
      ) {
        updatedContract.spacesOccupied = Number(noOfSpace[0]);
      }
      updateContract(updatedContract);
    };

    const updateContract = async (updatedContract: Partial<ContractEntity>) => {
      if (data?.isEnabled === updatedContract.isEnabled) return;

      setParamsUrl({ clicked: undefined });

      updateContractCall({
        variables: {
          contractId: contract._id,
          input: updatedContract,
        },
      }).then(({ data }) => {
        setData(data?.updateContract || null);
      });
    };

    const fetchProducts = useCallback(async () => {
      const products = await productsCall({
        variables: {
          parkingId: contract.parkingId,
          category: contract.category as ProductCategoryEnum,
          limit: 999,
        },
      });

      return products.data?.products.list;
    }, [contract.category, contract.parkingId]);

    useEffect(() => {
      fetchProducts().then((products) => setProductOptions(products || []));
    }, [contract.category, contract.parkingId]);

    const status = contract.isEnabled ? 'ACTIVE' : 'SUSPENDED';
    const isStopped = contract?.stopDate
      ? DateTime.fromISO(contract.stopDate).diffNow().as('milliseconds') < 0
      : false;
    const isUpcoming = DateTime.fromISO(contract.startDate) > DateTime.now();
    const states: [ContractBadgesType.Data.State] = [status];
    if (isUpcoming) states.push('UPCOMING');
    if (isStopped) states.push('EXPIRED');
    if (isStopped || isUpcoming) states.shift();
    if (contract.isInside) states.push('PRESENT');
    const badges = useContractBadges({ states });

    return (
      <Widget.Group
        config={{
          title: widget?.title,
          backtitle: !!widget?.title,
        }}
        state={widget?.state}
      >
        <Zone
          config={{
            zones: [['profil'], ['segmented'], ['details']],
            rows: ['min-content', 'min-content', '1fr'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area config={{ area: 'profil' }}>
            <Widget>
              <ProfilDetails
                handleEvent={{ submit: handleSubmit }}
                data={{
                  icon: iconGlobalEntity['masterPoolContract'],
                  form,
                  badges: badges,
                }}
                config={{
                  edit: config.PORTAL !== 'tenant',
                  form: {
                    title1: {
                      name: 'name2',
                      placeholder: t('name2'),
                    },
                    title2: {
                      name: 'name1',
                      placeholder: t('name1'),
                    },
                    description1: {
                      name: 'code',
                      placeholder: t('code'),
                      edit: false,
                    },
                    description2: {
                      name: 'reference',
                      placeholder: t('reference'),
                    },
                    group1: [
                      {
                        name: 'product',
                        label: t('product'),
                        element: {
                          type: 'select',
                          items: productOptions.map((product) => ({
                            label: t(`product-${product._id}-name`),
                            value: product._id,
                          })),
                        },
                        edit: false,
                      },
                      {
                        name: 'articleCode',
                        label: t('codePMS'),
                        element: {
                          type: 'select',
                          items: contract
                            .product!.externalPayload?.ArticleCodes?.filter(
                              (code) =>
                                code.status === ArticleCodeStatus.Active,
                            )
                            .map(({ code }) => ({
                              label: code,
                              value: code,
                            })),
                        },
                      },
                      {
                        name: 'noOfSpace',
                        label: t('noOfSpace'),
                        element: { type: 'counter' },
                        render: (value) =>
                          `${value[0].replace('/', '')} / ${value[1].replace(
                            '/',
                            '',
                          )}`,
                      },
                      {
                        name: 'startDate',
                        label: t('startDate'),
                        element: { type: 'datetime', max: contract.stopDate },
                        render: (value: string) =>
                          DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                      },
                      {
                        name: 'endDate',
                        label: t('endDate'),
                        element: {
                          type: 'datetime',
                          min: contract.startDate,
                        },
                        render: (value: string) =>
                          DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                      },
                      {
                        name: 'description',
                        label: t('description'),
                        element: { type: 'textarea' },
                      },
                    ],
                    group2: [
                      {
                        name: 'streetName',
                        label: t('address1'),
                        element: { type: 'input' },
                      },
                      {
                        name: 'streetNumber',
                        label: t('address2'),
                        element: { type: 'input' },
                      },
                      {
                        name: 'zipCode',
                        label: t('zipCode'),
                        element: { type: 'input' },
                      },
                      {
                        name: 'city',
                        label: t('city'),
                        element: { type: 'input' },
                      },
                      {
                        name: 'country',
                        label: t('country'),
                        element: {
                          type: 'select',
                          items: countryOptions,
                        },
                      },
                    ],
                  },
                  actions:
                    config.PORTAL !== 'tenant' ? (
                      <PoolContractAction
                        config={{ size: 'xlarge' }}
                        initialState={{
                          id: contract._id,
                          parkingId: contract.parkingId,
                          isStopped: isStopped,
                          status: contract.isEnabled,
                        }}
                      />
                    ) : undefined,
                }}
              />
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'segmented' }}>
            <Widget>
              <Segmented
                handleEvent={{ option: setSegmented }}
                data={{ selected: segmented }}
                config={{
                  size: 'large',
                  options: [
                    { label: t('admins'), value: 'admin' },
                    { label: t('sessions'), value: 'session' },
                    { label: t('history'), value: 'history' },
                  ],
                }}
              />
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'details' }}>
            {contract && (
              <>
                {segmented === 'admin' && <PoolAdminList contract={contract} />}
                {segmented === 'session' && (
                  <SessionList
                    overrideGQL={contractSessionsGql}
                    parkingId={contract.parkingId}
                    contractId={contract._id}
                    category={contract.category}
                  />
                )}
                {segmented === 'history' && (
                  <ContractHistoryList contractId={contract._id} />
                )}
              </>
            )}
          </Zone.Area>
        </Zone>
      </Widget.Group>
    );
  },
);
